/**
 * Activity Mapper Utility
 *
 * Transforms backend Activity responses (from /activities endpoint) to frontend ActivityItem format.
 * Includes mappings for:
 * - Backend activity_type → Frontend ActivityType
 * - Actor names → Display names
 * - Entity types → CTA generation
 */

import { formatDocumentType } from "@/utils/format-document-type";
import type { BackendActivity } from "@/data/api/activities";
import { stripUuids } from "@/utils/strip-uuids";
import { getActorDisplayName } from "@/utils/activity-labels";
import type {
    ActivityItem,
    ActivityType,
    ActivityAudience,
    ActivityAttachment,
    ActivityLink,
    ActivityMetadata,
} from "@/types/activity";

// ============================================================================
// Action Type Mapping
// ============================================================================

/**
 * Maps backend ActivityType enum values to frontend ActivityType
 * Backend has 37+ activity types, frontend has 8 display types
 */
export const ACTION_TYPE_MAP: Record<string, ActivityType> = {
    // Email processing → email_new_shipment_request
    email_received: "email_new_shipment_request",
    email_classified: "email_new_shipment_request",
    email_processed: "email_new_shipment_request",
    // Email requiring attention (low confidence, unknown sender, suspicious keywords)
    email_flagged: "email_attention_required",

    // Document processing → document_updated
    document_uploaded: "document_updated",
    document_classified: "document_updated",
    document_extracted: "document_updated",
    document_validated: "document_updated",
    document_linked: "document_updated",
    document_processed: "document_updated",
    document_validation_passed: "document_updated",
    document_modified: "document_updated",
    document_version_created: "document_updated",
    // Document errors → resolution_invite
    document_extraction_failed: "resolution_invite",
    document_validation_failed: "resolution_invite",
    document_timing_warning: "resolution_invite",

    // Expedition updates → status_update or expedition_modified
    expedition_updated: "status_update",
    expedition_modified: "expedition_modified",
    expedition_status_changed: "expedition_status_changed",
    expedition_alert: "status_update",
    task_completed: "status_update",
    shipment_closed: "status_update",

    // Booking workflow
    booking_validated: "status_update",
    booking_missing: "resolution_invite",

    // Departure/Arrival → status_update
    departure_confirmed: "status_update",
    arrival_validated: "status_update",
    delivery_confirmed: "status_update",
    delivery_discrepancy: "resolution_invite",

    // Delays and warnings
    container_delay_alert: "customs_issue",
    container_delay_info: "status_update",
    date_coherence_warning: "resolution_invite",

    // Customs → customs_issue
    customs_in_progress: "customs_issue",
    customs_issue_alert: "customs_issue",
    post_clearance_complete: "seal_change",

    // Notifications
    notification_sent: "status_update",
    notification_failed: "resolution_invite",

    // Agent delegation
    delegation_started: "status_update",
    delegation_completed: "status_update",
    delegation_failed: "resolution_invite",

    // Queries (typically internal, but map to status_update)
    query_executed: "status_update",
    search_performed: "status_update",

    // Errors and attention
    error_occurred: "resolution_invite",
    attention_required: "resolution_invite",
    ai_adaptive_proposal: "status_update",

    // Tracking updates
    tracking_updated: "status_update",
    container_status_changed: "status_update",

    // Issue management
    issue_reported: "resolution_invite",
    issue_resolved: "status_update",
    issue_created: "resolution_invite",
    issue_escalated: "resolution_invite",

    // Cross-check
    cross_check_executed: "resolution_invite",
    cross_check_issue_detected: "resolution_invite",

    // Contact (address book)
    contact_created: "contact_added",
    company_created: "contact_added",

    // Notes
    note_created: "status_update",

    // Single-doc check
    single_check_executed: "document_updated",

    // Review Center
    review_item_created: "resolution_invite",
    review_item_resolved: "status_update",
};

/**
 * Default activity type when no mapping is found
 */
const DEFAULT_ACTIVITY_TYPE: ActivityType = "status_update";

/**
 * Gets activity type with case-insensitive lookup
 * Handles PostgreSQL enum case sensitivity issues (NAME vs VALUE)
 */
function getActivityType(activityType: string | null): ActivityType {
    if (!activityType) return DEFAULT_ACTIVITY_TYPE;

    // Try direct lookup first
    if (ACTION_TYPE_MAP[activityType]) {
        return ACTION_TYPE_MAP[activityType];
    }

    // Try lowercase (handles UPPERCASE enum names from PostgreSQL)
    const lowercase = activityType.toLowerCase();
    if (ACTION_TYPE_MAP[lowercase]) {
        return ACTION_TYPE_MAP[lowercase];
    }

    return DEFAULT_ACTIVITY_TYPE;
}

// ============================================================================
// Audience Mapping
// ============================================================================

/**
 * Maps backend audience values to frontend ActivityAudience
 */
export function mapAudience(audience: string | null): ActivityAudience {
    if (!audience) return "shipper"; // Default to shipper if no audience

    const audienceMap: Record<string, ActivityAudience> = {
        shipper: "shipper",
        receiver: "receiver",
        freightForwarder: "freightForwarder",
        carrier: "shipper", // Map carrier to shipper for now
        customsBroker: "freightForwarder", // Map customsBroker to freightForwarder
        all: "shipper", // Default to shipper for "all"
    };

    return audienceMap[audience] || "shipper";
}

// ============================================================================
// CTA Generation
// ============================================================================

/**
 * Generates action CTAs based on activity type and entity
 */
export function generateActionsForActivity(
    activity: BackendActivity
): ActivityLink[] {
    const { entity_type, entity_id, details } = activity;
    // Normalize activity_type to lowercase for case-insensitive comparison
    const activity_type = activity.activity_type?.toLowerCase() || "";
    const actions: ActivityLink[] = [];

    // Extract container ID from details if available
    const containerId =
        (details?.container_id as string) ||
        (details?.containerId as string) ||
        activity.expedition_id ||
        entity_id;

    // Container ID for document URLs (without entity_id fallback, since entity_id is the document ID)
    const docContainerId =
        (details?.container_id as string) ||
        (details?.containerId as string) ||
        activity.expedition_id ||
        null;

    switch (activity_type) {
        // Email actions
        case "email_processed":
        case "email_received":
        case "email_classified":
            if (details?.routeFrom && details?.routeTo) {
                actions.push({
                    label: "Crea container",
                    href: `/shipper/containers/new?routeFrom=${encodeURIComponent(
                        String(details.routeFrom)
                    )}&routeTo=${encodeURIComponent(String(details.routeTo))}`,
                });
            }
            if (entity_type === "email" && entity_id) {
                actions.push({
                    label: "Vedi email",
                    href: "#", // Not used when modalType is set
                    modalType: "email",
                    modalId: entity_id,
                });
            }
            break;

        // Document actions
        case "document_uploaded":
        case "document_classified":
        case "document_extracted":
        case "document_validated":
        case "document_processed":
        case "document_version_created":
            actions.push({
                label: "Vedi documento",
                href: docContainerId
                    ? `/shipper/containers/${docContainerId}/documents/${entity_id}?mode=manager`
                    : `/shipper/documents/${entity_id}`,
            });
            if (docContainerId) {
                actions.push({
                    label: "Vedi Container",
                    href: `/shipper/containers/${docContainerId}`,
                });
            }
            break;

        case "document_extraction_failed":
        case "document_validation_failed":
        case "document_timing_warning":
            actions.push({
                label: "Correggi documento",
                href: docContainerId
                    ? `/shipper/containers/${docContainerId}/documents/${entity_id}?mode=review`
                    : `/shipper/documents/${entity_id}?mode=edit`,
            });
            break;

        // Customs issues
        case "customs_issue_alert":
        case "customs_in_progress":
            actions.push({
                label: "Gestisci pratica",
                href: `/shipper/containers/${containerId}?tab=documents`,
            });
            break;

        // Expedition/Container updates
        case "expedition_updated":
        case "departure_confirmed":
        case "arrival_validated":
        case "delivery_confirmed":
            actions.push({
                label: "Vedi timeline",
                href: `/shipper/containers/${containerId}?tab=timeline`,
            });
            break;

        // Delays
        case "container_delay_alert":
        case "container_delay_info":
        case "delay_detected":
        case "tracking_updated":
        case "container_status_changed":
            actions.push({
                label: "Dettagli tracking",
                href: `/shipper/containers/${containerId}?tab=tracking`,
            });
            // TODO: "Notifica stakeholder" button — needs notification action endpoint
            break;

        // Phase transition — link to container + upload missing docs
        case "phase_transition": {
            actions.push({
                label: "Vedi container",
                href: `/shipper/containers/${containerId}`,
            });
            // If there are missing required docs, add upload CTA
            const reqDocs = details?.required_docs_status as Array<{ present: boolean }> | undefined;
            const hasMissing = reqDocs?.some(d => !d.present);
            if (hasMissing && containerId) {
                actions.push({
                    label: "Carica documenti mancanti",
                    href: `/shipper/containers/${containerId}?tab=documents`,
                });
            }
            break;
        }

        // Container created — link to container + email origin
        case "expedition_created": {
            actions.push({
                label: "Vedi container",
                href: `/shipper/containers/${containerId}`,
            });
            // If created from email, add link to email
            if (details?.source_email_id) {
                actions.push({
                    label: "Vedi email origine",
                    href: "#",
                    modalType: "email",
                    modalId: details.source_email_id as string,
                });
            }
            break;
        }

        // Post clearance
        case "post_clearance_complete":
        case "shipment_closed":
            actions.push({
                label: "Vedi riepilogo",
                href: `/shipper/containers/${containerId}?tab=summary`,
            });
            break;

        // Cross-check issues: link to document detail (first failure with doc) + issues tab with Document Query filter
        case "cross_check_executed":
        case "cross_check_issue_detected": {
            const validationFailures = details?.validation_failures as Array<{
                source_document_id?: string;
                source_document_name?: string;
                target_document_id?: string;
                target_document_name?: string;
            }> | undefined;
            const firstWithDoc = validationFailures?.find(
                (f) => f.source_document_id || f.target_document_id
            );
            const docId = firstWithDoc?.source_document_id || firstWithDoc?.target_document_id;
            if (docId && containerId) {
                actions.push({
                    label: "Vedi documento",
                    href: `/shipper/containers/${containerId}/documents/${docId}?mode=manager`,
                });
            }
            if (containerId) {
                actions.push({
                    label: "Vedi issue",
                    href: `/shipper/containers/${containerId}?tab=issues&issue_type=cross_check_document_check`,
                });
            }
            break;
        }

        // Contact / Business Partner added to Address Book
        case "contact_created":
        case "company_created": {
            const audience = activity.audience?.toLowerCase() || "shipper";
            const basePath =
                audience === "receiver"
                    ? "/receiver"
                    : audience === "freightforwarder"
                      ? "/freight-forwarder"
                      : "/shipper";
            actions.push({
                label: "Vedi Address Book",
                href: `${basePath}/address-book`,
            });
            break;
        }

        // Review Center
        case "review_item_created":
            actions.push({
                label: "Apri Review Center",
                href: "/shipper/review-center",
            });
            break;
        case "review_item_resolved":
            if (containerId) {
                actions.push({
                    label: "Vedi container",
                    href: `/shipper/containers/${containerId}`,
                });
            }
            break;

        // Issue management
        case "issue_created":
        case "issue_reported":
        case "issue_escalated":
            if (containerId) {
                const severity = (details?.severity as string)?.toLowerCase();
                actions.push({
                    label: "Vedi Issue",
                    href: `/shipper/containers/${containerId}?tab=issues`,
                    severity: (severity === "critical" || severity === "high" || severity === "medium" || severity === "low")
                        ? severity
                        : undefined,
                });
                // "Prendi in carico" for new/open issues
                if (details?.issue_id) {
                    actions.push({
                        label: "Prendi in carico",
                        href: `/shipper/containers/${containerId}?tab=issues&issue=${details.issue_id}`,
                    });
                }
            }
            break;
        case "issue_resolved":
        case "issue_status_changed":
            if (containerId) {
                actions.push({
                    label: "Vedi Issue",
                    href: `/shipper/containers/${containerId}?tab=issues`,
                });
            }
            break;

        // Resolution invites
        case "attention_required":
        case "error_occurred":
            if (entity_type === "document") {
                actions.push({
                    label: "Risolvi problema",
                    href: docContainerId
                        ? `/shipper/containers/${docContainerId}/documents/${entity_id}?mode=manager`
                        : `/shipper/documents/${entity_id}`,
                });
            } else if (entity_type === "expedition" || entity_type === "container") {
                actions.push({
                    label: "Risolvi problema",
                    href: `/shipper/containers/${entity_id}`,
                });
            }
            break;

        default:
            // Generic action based on entity type
            if (entity_type === "document") {
                actions.push({
                    label: "Vedi documento",
                    href: docContainerId
                        ? `/shipper/containers/${docContainerId}/documents/${entity_id}?mode=manager`
                        : `/shipper/documents/${entity_id}`,
                });
            } else if (entity_type === "expedition" || entity_type === "container") {
                actions.push({
                    label: "Vedi container",
                    href: `/shipper/containers/${entity_id}`,
                });
            } else if (entity_type === "email" && entity_id) {
                actions.push({
                    label: "Vedi email",
                    href: "#", // Not used when modalType is set
                    modalType: "email",
                    modalId: entity_id,
                });
            }
            break;
    }

    return actions;
}

// ============================================================================
// Attachment Extraction
// ============================================================================

/**
 * Extracts attachments from activity details
 * @param details - Activity details from backend
 * @param activityType - Optional activity type (e.g. for cross-check file names from validation_failures)
 */
export function extractAttachments(
    details: Record<string, unknown> | null,
    activityType?: string | null
): ActivityAttachment[] {
    if (!details) return [];

    const attachments: ActivityAttachment[] = [];

    // Check for attachments array in details
    const rawAttachments = details.attachments as
        | Array<{
              name?: string;
              filename?: string;
              type?: string;
              content_type?: string;
              size?: number;
              sizeKB?: number;
          }>
        | undefined;

    if (Array.isArray(rawAttachments)) {
        for (const att of rawAttachments) {
            const name = att.name || att.filename || "Allegato";
            const contentType = att.type || att.content_type || "";

            // Determine type from content type or extension
            let type: ActivityAttachment["type"] = "other";
            if (contentType.includes("pdf") || name.endsWith(".pdf")) {
                type = "pdf";
            } else if (
                contentType.includes("excel") ||
                name.endsWith(".xls")
            ) {
                type = "xls";
            } else if (
                contentType.includes("spreadsheet") ||
                name.endsWith(".xlsx")
            ) {
                type = "xlsx";
            } else if (contentType.includes("text") || name.endsWith(".txt")) {
                type = "txt";
            }

            attachments.push({
                name,
                type,
                sizeKB: att.sizeKB || (att.size ? Math.round(att.size / 1024) : undefined),
            });
        }
    }

    // Check for single document in details
    if (details.document_name || details.filename) {
        const name = (details.document_name || details.filename) as string;
        attachments.push({
            name,
            type: name.endsWith(".pdf") ? "pdf" : "other",
        });
    }

    // Cross-check: show document names from validation_failures (file del cross-check)
    const typeLower = activityType?.toLowerCase();
    if (typeLower === "cross_check_executed" || typeLower === "cross_check_issue_detected") {
        const validationFailures = details.validation_failures as Array<{
            source_document_name?: string;
            target_document_name?: string;
        }> | undefined;
        const seen = new Set<string>();
        if (Array.isArray(validationFailures)) {
            for (const f of validationFailures) {
                for (const n of [f.source_document_name, f.target_document_name]) {
                    if (n && !seen.has(n)) {
                        seen.add(n);
                        attachments.push({
                            name: n,
                            type: n.endsWith(".pdf") ? "pdf" : "other",
                        });
                    }
                }
            }
        }
    }

    return attachments;
}

// ============================================================================
// Metadata Extraction
// ============================================================================

/**
 * Extracts metadata from activity details
 */
export function extractMetadata(
    activity: BackendActivity
): ActivityMetadata | undefined {
    const { details, entity_type, entity_id, attention_reason, expedition_id, summary, activity_type } = activity;

    const metadata: ActivityMetadata = {};

    // For email activities, use the activity.summary as emailSummary
    // This contains the AI-generated summary of the email content
    const isEmailActivity = activity_type?.startsWith("email_");
    if (isEmailActivity && summary && summary !== activity.title) {
        // Strip any residual HTML tags from summary
        metadata.emailSummary = summary.replace(/<[^>]+>/g, '').trim();
    }

    // Entity-based metadata
    if (entity_type === "container" || entity_type === "expedition") {
        metadata.containerId = entity_id;
    } else if (entity_type === "document") {
        metadata.documentId = entity_id;
    }

    // Use expedition_id if available
    if (expedition_id) {
        metadata.containerId = expedition_id;
    }

    // Use top-level container_number from API (joined from containers table)
    if (activity.container_number) {
        metadata.containerNumber = activity.container_number;
    }

    // Extract from details
    if (details) {
        if (details.container_id)
            metadata.containerId = details.container_id as string;
        if (details.containerId)
            metadata.containerId = details.containerId as string;
        if (details.container_number)
            metadata.containerNumber = details.container_number as string;
        if (details.containerNumber)
            metadata.containerNumber = details.containerNumber as string;
        if (details.route_from || details.routeFrom)
            metadata.routeFrom = (details.route_from || details.routeFrom) as string;
        if (details.route_to || details.routeTo)
            metadata.routeTo = (details.route_to || details.routeTo) as string;
        if (details.shipping_company || details.shippingCompany)
            metadata.shippingCompany = (details.shipping_company ||
                details.shippingCompany) as string;
        if (details.commodity) metadata.commodity = details.commodity as string;
        if (details.document_id)
            metadata.documentId = details.document_id as string;
        if (details.documentId)
            metadata.documentId = details.documentId as string;
        if (details.document_type)
            metadata.documentType = formatDocumentType(details.document_type as string);
        if (details.documentType)
            metadata.documentType = formatDocumentType(details.documentType as string);
        if (details.seal_id || details.sealId)
            metadata.sealId = (details.seal_id || details.sealId) as string;
        if (details.order_id || details.orderId)
            metadata.orderId = (details.order_id || details.orderId) as string;
        if (details.receiver_id || details.receiverId)
            metadata.receiverId = (details.receiver_id || details.receiverId) as string;
        if (details.shipper_id || details.shipperId)
            metadata.shipperId = (details.shipper_id || details.shipperId) as string;

        // Email metadata
        if (details.from || details.email_from || details.emailFrom)
            metadata.emailFrom = (details.from || details.email_from || details.emailFrom) as string;
        if (details.subject || details.email_subject || details.emailSubject)
            metadata.emailSubject = (details.subject || details.email_subject || details.emailSubject) as string;
        // Only set emailSummary from details if not already set from activity.summary
        if (!metadata.emailSummary && (details.summary || details.email_summary || details.emailSummary))
            metadata.emailSummary = ((details.summary || details.email_summary || details.emailSummary) as string).replace(/<[^>]+>/g, '').trim();
        if (details.received_at || details.receivedAt || details.email_received_at || details.emailReceivedAt)
            metadata.emailReceivedAt = (details.received_at || details.receivedAt || details.email_received_at || details.emailReceivedAt) as string;
        if (details.synced_at || details.syncedAt)
            metadata.emailSyncedAt = (details.synced_at || details.syncedAt) as string;
        // Account email (integrated email account that received the message)
        if (details.account_email || details.accountEmail)
            metadata.accountEmail = (details.account_email || details.accountEmail) as string;

        // Document metadata
        if (details.file_name || details.fileName || details.document_name || details.documentName)
            metadata.documentName = (details.file_name || details.fileName || details.document_name || details.documentName) as string;
        // Document type from classification (new_type is the classification result)
        if (details.new_type || details.document_type || details.documentType)
            metadata.documentType = formatDocumentType(
                (details.new_type || details.document_type || details.documentType) as string
            );
        if (details.document_category || details.documentCategory || details.category)
            metadata.documentCategory = (details.document_category || details.documentCategory || details.category) as string;
        if (details.upload_source || details.uploadSource || details.source)
            metadata.uploadSource = (details.upload_source || details.uploadSource || details.source) as string;
        if (details.uploaded_by || details.uploadedBy || details.user_email || details.userEmail || details.actor || details.created_by_name || details.created_by_email)
            metadata.uploadedBy = (details.uploaded_by || details.uploadedBy || details.created_by_name || details.created_by_email || details.user_email || details.userEmail || details.actor) as string;

        // Issue metadata
        if (details.reported_by || details.reportedBy)
            metadata.reportedBy = (details.reported_by || details.reportedBy) as string;
        if (details.issue_type || details.issueType)
            metadata.issueType = (details.issue_type || details.issueType) as string;
        if (details.severity)
            metadata.issueSeverity = details.severity as string;

        // Contact metadata
        if (details.contact_name || details.contactName)
            metadata.contactName = (details.contact_name || details.contactName) as string;
        if (details.contact_email || details.contactEmail)
            metadata.contactEmail = (details.contact_email || details.contactEmail) as string;
        if (details.contact_company_name || details.contactCompanyName)
            metadata.contactCompanyName = (details.contact_company_name || details.contactCompanyName) as string;
        if (details.relationship_type || details.relationshipType)
            metadata.contactRelationshipType = (details.relationship_type || details.relationshipType) as string;

        // Expedition modified metadata
        if (details.updated_by || details.updatedBy)
            metadata.updatedBy = (details.updated_by || details.updatedBy) as string;
        if (details.updated_fields || details.updatedFields)
            metadata.updatedFields = (details.updated_fields || details.updatedFields) as string[];

        // Status change metadata
        if (details.old_status || details.oldStatus)
            metadata.oldStatus = (details.old_status || details.oldStatus) as string;
        if (details.new_status || details.newStatus)
            metadata.newStatus = (details.new_status || details.newStatus) as string;

        // Delay metadata
        if (details.delay_hours || details.delayHours)
            metadata.delayHours = Number(details.delay_hours || details.delayHours);
        if (details.original_eta || details.originalEta)
            metadata.originalEta = (details.original_eta || details.originalEta) as string;
        if (details.current_eta || details.currentEta)
            metadata.currentEta = (details.current_eta || details.currentEta) as string;
        if (details.vessel_name || details.vesselName)
            metadata.vesselName = (details.vessel_name || details.vesselName) as string;

        // Cross-check metadata
        if (details.passed != null)
            metadata.crossCheckPassed = Number(details.passed);
        if (details.failed != null)
            metadata.crossCheckFailed = Number(details.failed);
        if (details.skipped != null)
            metadata.crossCheckSkipped = Number(details.skipped);
        if (details.missing_documents != null)
            metadata.missingDocuments = Number(details.missing_documents);
        if (Array.isArray(details.validation_failures))
            metadata.validationFailures = details.validation_failures as ActivityMetadata["validationFailures"];

        // Risk metadata
        if (details.overall_risk != null || details.overallRisk != null)
            metadata.overallRisk = Number(details.overall_risk ?? details.overallRisk);
        if (details.risk_factors || details.riskFactors)
            metadata.riskFactors = (details.risk_factors || details.riskFactors) as Record<string, number>;
        if (details.active_issues_count != null || details.activeIssuesCount != null)
            metadata.activeIssuesCount = Number(details.active_issues_count ?? details.activeIssuesCount);

        // Phase transition metadata
        if (details.old_phase || details.oldPhase)
            metadata.oldPhase = (details.old_phase || details.oldPhase) as string;
        if (details.new_phase || details.newPhase)
            metadata.newPhase = (details.new_phase || details.newPhase) as string;
        if (details.issues_closed != null || details.issuesClosed != null)
            metadata.issuesClosed = Number(details.issues_closed ?? details.issuesClosed);
        if (details.trigger_source)
            metadata.triggerSource = details.trigger_source as string;
        if (Array.isArray(details.required_docs_status))
            metadata.requiredDocsStatus = details.required_docs_status as ActivityMetadata["requiredDocsStatus"];

        // Expedition modified — before/after field changes
        if (details.field_changes)
            metadata.fieldChanges = details.field_changes as ActivityMetadata["fieldChanges"];

        // Container creation context
        if (details.shipping_line)
            metadata.shippingLine = details.shipping_line as string;
        if (details.creation_source)
            metadata.creationSource = details.creation_source as string;
        if (details.booking_number)
            metadata.bookingNumber = details.booking_number as string;
    }

    // Extract container number from attention_reason if present
    // Pattern: "container_created_automatically:MSCU2097473" or just "MSCU2097473"
    if (!metadata.containerNumber && attention_reason) {
        // ISO 6346 container number pattern: 4 letters + 7 digits
        const containerMatch = attention_reason.match(/[A-Z]{4}\d{7}/i);
        if (containerMatch) {
            metadata.containerNumber = containerMatch[0].toUpperCase();
        }
    }

    // Never display UUIDs as container numbers — strip if it looks like a UUID
    if (metadata.containerNumber && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(metadata.containerNumber)) {
        delete metadata.containerNumber;
    }

    // Return undefined if no metadata found
    return Object.keys(metadata).length > 0 ? metadata : undefined;
}

// ============================================================================
// Title Generation for Attention-Required Activities
// ============================================================================

/**
 * Issue type labels for parseAttentionReasons fallback
 */
const ISSUE_TYPE_LABELS: Record<string, string> = {
    carrier_delay: "ritardo vettore",
    documentation_query: "verifica documentale",
    single_document_check: "errore documento (singolo)",
    cross_check_document_check: "errore documento (cross-check)",
    customs_hold: "blocco doganale",
    temperature_deviation: "deviazione temperatura",
    damage_report: "segnalazione danni",
    weight_discrepancy: "discrepanza peso",
    missing_document: "documento mancante",
    other: "altra segnalazione",
};

/**
 * Attention reason codes and their user-friendly labels
 */
const ATTENTION_REASON_LABELS: Record<string, string> = {
    "urgency:high": "Alta priorità",
    "urgency:critical": "Priorità critica",
    "unknown_sender": "Mittente sconosciuto",
    "suspicious_keywords": "Parole chiave sospette",
    "low_confidence": "Bassa confidenza classificazione",
    "unclassified_attachment": "Allegato non classificato",
    "multiple_attachments": "Allegati multipli da verificare",
    "large_attachment": "Allegato di grandi dimensioni",
    "missing_reference": "Riferimento mancante",
    "container_created_automatically": "Container creato automaticamente",
    "unknown_sender_domain": "Dominio mittente sconosciuto",
    "keyword": "Parola chiave rilevata",
    "container_number_without_booking": "Container senza prenotazione",
    "bl_not_found_in_system": "B/L non trovato nel sistema",
    "multiple_containers_created": "Più container creati",
    "container_added_to_booking_via_bl": "Container aggiunto via B/L",
    "references_without_container_number": "Riferimenti senza numero container",
    "folder_creation_failed": "Creazione cartella fallita",
    // Issue auto-detection reasons (structured format)
    "issue_type:carrier_delay": "Ritardo vettore rilevato",
    "issue_type:documentation_query": "Verifica documentale rilevata",
    "issue_type:single_document_check": "Errore documento (singolo) rilevato",
    "issue_type:cross_check_document_check": "Errore documento (cross-check) rilevato",
    "issue_type:customs_hold": "Blocco doganale rilevato",
    "issue_type:temperature_deviation": "Deviazione temperatura rilevata",
    "issue_type:damage_report": "Segnalazione danni rilevata",
    "issue_type:weight_discrepancy": "Discrepanza peso rilevata",
    "issue_type:missing_document": "Documento mancante rilevato",
    "issue_type:other": "Segnalazione rilevata",
    // Severity labels
    "severity:critical": "Severità critica",
    "severity:high": "Severità alta",
};

/**
 * Parses attention_reason string and returns user-friendly labels
 * Input format: "urgency:high, unclassified_attachment:bl_draft.pdf"
 */
function parseAttentionReasons(attentionReason: string | null): string[] {
    if (!attentionReason) return [];

    const reasons: string[] = [];
    const parts = attentionReason.split(",").map(p => p.trim());

    for (const part of parts) {
        // Handle "key:value" format
        const colonIndex = part.indexOf(":");
        if (colonIndex > 0) {
            const key = part.substring(0, colonIndex);
            const value = part.substring(colonIndex + 1);

            // Check if we have a label for this key
            if (ATTENTION_REASON_LABELS[key]) {
                reasons.push(ATTENTION_REASON_LABELS[key]);
            } else if (ATTENTION_REASON_LABELS[`${key}:${value}`]) {
                reasons.push(ATTENTION_REASON_LABELS[`${key}:${value}`]);
            } else if (key === "unclassified_attachment") {
                reasons.push(`Allegato "${value}" da classificare`);
            } else if (key === "urgency") {
                reasons.push(`Priorità: ${value}`);
            } else if (key === "Auto-detected") {
                const issueLabel = ISSUE_TYPE_LABELS[value] || value.replace(/_/g, ' ');
                reasons.push(`Rilevato: ${issueLabel}`);
            } else {
                // Generic format
                reasons.push(`${key}: ${value}`);
            }
        } else if (ATTENTION_REASON_LABELS[part]) {
            reasons.push(ATTENTION_REASON_LABELS[part]);
        } else {
            reasons.push(part);
        }
    }

    return reasons;
}

// ============================================================================
// Document Title Generation (Italian)
// ============================================================================

/**
 * Generates Italian document card titles with document type and context.
 * Returns null if no override needed (fallback to backend title).
 */
function generateDocumentTitle(activity: BackendActivity): string | null {
    const { details, activity_type } = activity;
    const typeLower = activity_type?.toLowerCase() || "";

    // Extract document type label
    const rawDocType = details?.document_type || details?.new_type || details?.document_type_code;
    const docType = rawDocType ? formatDocumentType(String(rawDocType)) : null;
    const fileName = (details?.file_name || details?.document_name) as string | undefined;

    switch (typeLower) {
        case "document_uploaded": {
            const label = docType || "Documento";
            return fileName ? `${label} caricato: ${fileName}` : `${label} caricato`;
        }
        case "document_classified": {
            const oldType = details?.old_type ? formatDocumentType(String(details.old_type)) : null;
            const newType = details?.new_type ? formatDocumentType(String(details.new_type)) : null;
            if (oldType && newType) {
                return `Documento riclassificato: ${oldType} → ${newType}`;
            }
            return newType ? `Documento classificato: ${newType}` : "Documento classificato";
        }
        case "document_version_created": {
            const label = docType || "Documento";
            const version = details?.new_version;
            const origName = details?.original_file_name || details?.original_document_name;
            if (version && origName) {
                return `Nuova versione ${label}: v${version}`;
            }
            return `Nuova versione ${label}`;
        }
        case "document_modified": {
            const label = docType || "Documento";
            return `${label} modificato`;
        }
        case "document_extraction_failed": {
            return fileName ? `Estrazione fallita: ${fileName}` : "Estrazione documento fallita";
        }
        case "document_validation_failed": {
            const label = docType || "Documento";
            const criticalCount = details?.critical_issues ?? details?.critical_found ?? 0;
            return `Validazione ${label}: ${criticalCount} problemi`;
        }
        case "cross_check_executed":
        case "cross_check_issue_detected": {
            const failed = Number(details?.failed ?? 0);
            const missing = Number(details?.missing_documents ?? 0);
            const totalProblems = failed + missing;
            if (totalProblems > 0) {
                return `Cross-check: ${totalProblems} problemi trovati`;
            }
            return "Cross-check completato";
        }
        case "single_check_executed": {
            const label = docType || "Documento";
            const issuesCreated = Number(details?.total_issues_created ?? 0);
            if (issuesCreated > 0) {
                return `Validazione ${label}: ${issuesCreated} problemi`;
            }
            return `Validazione ${label} completata`;
        }
        default:
            return null;
    }
}

// ============================================================================
// Tracking Title Generation (Italian)
// ============================================================================

/**
 * Generates Italian tracking card titles with context.
 * Returns null if no override needed (fallback to backend title).
 */
function generateTrackingTitle(activity: BackendActivity): string | null {
    const { details, activity_type } = activity;
    const typeLower = activity_type?.toLowerCase() || "";

    switch (typeLower) {
        case "expedition_created": {
            const containerNum = (details?.container_number || activity.container_number) as string | undefined;
            return containerNum ? `Container creato: ${containerNum}` : "Container creato";
        }
        case "expedition_modified": {
            const updatedBy = details?.updated_by as string | undefined;
            return updatedBy ? `Container modificato da ${updatedBy}` : "Container modificato";
        }
        case "expedition_status_changed": {
            const oldSt = details?.old_status as string | undefined;
            const newSt = details?.new_status as string | undefined;
            if (oldSt && newSt) return `Stato cambiato: ${oldSt} -> ${newSt}`;
            return "Stato container cambiato";
        }
        case "phase_transition": {
            const oldPhase = details?.old_phase as string | undefined;
            const newPhase = details?.new_phase as string | undefined;
            if (oldPhase && newPhase) return `Fase: ${oldPhase} -> ${newPhase}`;
            return "Fase cambiata";
        }
        case "delay_detected": {
            const hours = details?.delay_hours;
            const containerNum = (details?.container_number || activity.container_number) as string | undefined;
            if (hours && containerNum) return `Ritardo: ${hours}h - ${containerNum}`;
            if (hours) return `Ritardo rilevato: ${hours}h`;
            return "Ritardo rilevato";
        }
        case "eta_updated": {
            const containerNum = (details?.container_number || activity.container_number) as string | undefined;
            return containerNum ? `ETA aggiornato: ${containerNum}` : "ETA aggiornato";
        }
        case "risk_alert_created": {
            const containerNum = (details?.container_number || activity.container_number) as string | undefined;
            const risk = details?.overall_risk;
            if (containerNum && risk != null) {
                const pct = Math.round(Number(risk) * 100);
                return `Rischio alto: ${containerNum} (${pct}%)`;
            }
            return null; // Keep backend title
        }
        default:
            return null;
    }
}

// ============================================================================
// Issue Title Generation (Italian)
// ============================================================================

/**
 * Generates Italian issue card titles with context.
 * Returns null if no override needed.
 */
function generateIssueTitle(activity: BackendActivity): string | null {
    const { details, activity_type } = activity;
    const typeLower = activity_type?.toLowerCase() || "";

    switch (typeLower) {
        case "issue_created":
        case "issue_reported": {
            const title = details?.issue_title as string | undefined;
            return title ? `Issue: ${title}` : "Issue creata";
        }
        case "issue_resolved": {
            const title = details?.issue_title as string | undefined;
            return title ? `Issue risolta: ${title}` : "Issue risolta";
        }
        case "issue_status_changed": {
            const title = details?.issue_title as string | undefined;
            return title ? `Issue aggiornata: ${title}` : "Issue aggiornata";
        }
        case "cross_check_issue_detected": {
            const ruleName = details?.rule_name as string | undefined;
            return ruleName ? `Discrepanza: ${ruleName}` : "Discrepanza trovata";
        }
        default:
            return null;
    }
}

/**
 * Generates a descriptive title for email_flagged activities
 */
export function generateFlaggedEmailTitle(activity: BackendActivity): string {
    parseAttentionReasons(activity.attention_reason); // May be used in future

    // Check for specific attention reasons to generate contextual titles
    if (activity.attention_reason?.includes("urgency:critical")) {
        return "Email critica da verificare";
    }
    if (activity.attention_reason?.includes("urgency:high")) {
        return "Email urgente da verificare";
    }
    if (activity.attention_reason?.includes("unknown_sender")) {
        return "Email da mittente sconosciuto";
    }
    if (activity.attention_reason?.includes("unclassified_attachment")) {
        return "Email con allegato da classificare";
    }
    if (activity.attention_reason?.includes("low_confidence")) {
        return "Email da verificare manualmente";
    }
    if (activity.attention_reason?.includes("container_created_automatically")) {
        return "Container creato - verifica richiesta";
    }

    // Default fallback
    return "Email richiede attenzione";
}

/**
 * Generates user-friendly description from attention_reason
 */
export function generateAttentionDescription(attentionReason: string | null): string | undefined {
    if (!attentionReason) return undefined;

    const reasons = parseAttentionReasons(attentionReason);
    if (reasons.length === 0) return undefined;

    return reasons.join(" • ");
}

// ============================================================================
// Main Mapper Function
// ============================================================================

/**
 * Transforms a backend Activity to a frontend ActivityItem
 * Uses the new unified /activities endpoint schema
 */
export function mapBackendToFrontend(activity: BackendActivity): ActivityItem {
    // Get display name for actor
    const actorDisplayName = getActorDisplayName(
        activity.actor_name || "system",
        activity.source_type
    );

    // Generate title - override with Italian contextual titles
    let title = activity.title || activity.summary;
    if (activity.activity_type === "email_flagged") {
        title = generateFlaggedEmailTitle(activity);
    } else {
        // Try contextual title overrides (Italian) by category
        const override =
            generateDocumentTitle(activity) ??
            generateTrackingTitle(activity) ??
            generateIssueTitle(activity);
        if (override) title = override;
    }
    // Strip UUIDs from title (legacy data may embed container_id instead of container_number)
    if (title) title = stripUuids(title);

    // Generate description - parse attention_reason into readable format
    const description = activity.attention_reason
        ? generateAttentionDescription(activity.attention_reason)
        : undefined;

    const baseMetadata = extractMetadata(activity) ?? {};
    const emailAttentionLabels =
        activity.activity_type === "email_flagged" && activity.attention_reason
            ? parseAttentionReasons(activity.attention_reason)
            : undefined;

    const metadataMerged: ActivityMetadata = { ...baseMetadata };
    if (emailAttentionLabels?.length) {
        metadataMerged.emailAttentionLabels = emailAttentionLabels;
    }

    return {
        id: activity.activity_id,
        audience: mapAudience(activity.audience),
        type: getActivityType(activity.activity_type),
        activityCategory: activity.activity_category, // For client-side filtering
        title,
        description,
        timestamp: new Date(activity.created_at),
        isRead: activity.is_read,
        attentionLevel: activity.attention_level,
        entityDeleted: activity.entity_deleted,
        actor: {
            name: actorDisplayName,
        },
        attachments: extractAttachments(activity.details, activity.activity_type),
        actions: generateActionsForActivity(activity),
        metadata: Object.keys(metadataMerged).length > 0 ? metadataMerged : undefined,
    };
}

/**
 * Transforms an array of backend activities to frontend format
 */
export function mapActivitiesToFrontend(
    activities: BackendActivity[]
): ActivityItem[] {
    return activities.map(mapBackendToFrontend);
}

/**
 * Helper to check if an activity needs attention (has any attention level set)
 */
export function activityNeedsAttention(activity: BackendActivity): boolean {
    return activity.attention_level !== null;
}
